import { AuthenticatedUser } from "@authentication/domain/models/authenticated-user.model";
import { GetLoggedUserUseCase } from "@authentication/domain/usecases/get-logged-user.usecase";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import { GlobalEntity } from "@entity/domain/models/global-entity.model";
import { GetAllGlobalEntitiesUseCase } from "@entity/domain/usecases/get-all-global-entities.usecase";
import { inject, injectable } from "inversify";
import { makeObservable, observable, runInAction } from "mobx";

export interface EntityApiScopeOption {
    value: string;
    label: string;
}

const scopeValues: string[] = [
    "employees:read",
    "employees:write",
    "documents:read",
    "entity:read",
    "entity:write",
    "entity_addresses:read",
    "entity_addresses:write",
    "entity_contacts:read",
    "entity_contacts:write",
    "entity_affiliations:read",
    "entity_affiliations:write",
    "creditors:read",
    "creditors:write",
    "suppliers:read",
    "suppliers:write",
    "materials:read",
    "materials:write",
    "revenues:read",
    "revenues:write",
    "costs:read",
    "costs:write",
    "entity_social_networks:read",
    "entity_social_networks:write",
    "entity_locals:read",
    "entity_locals:write",
    "entity_rooms:read",
    "entity_rooms:write",
    "entity_management_bodies:read",
    "entity_management_bodies:write",
    "entity_partners:read",
    "entity_partners:write",
    "entity_quality_certificates:read",
    "entity_quality_certificates:write",
    "entity_policy_protocols:read",
    "entity_policy_protocols:write",
    "entity_project_auditories:read",
    "entity_project_auditories:write",
    "entity_entity_auditories:read",
    "entity_entity_auditories:write",
    "beneficiaries:read",
    "beneficiaries:write",
    "services:read",
    "services:write",
    "derivations:read",
    "derivations:write",
    "projects:read",
    "projects:write",
];

@injectable()
export class EntityApiKeyFormViewModel extends BaseViewModel {
    @observable
    initialLoading: boolean = true;

    @observable
    globalEntities: GlobalEntity[] = [];

    readonly scopeOptions: EntityApiScopeOption[] = scopeValues.map(
        (value) => ({
            value,
            label: value,
        }),
    );

    constructor(
        @inject(GetLoggedUserUseCase)
        private readonly getLoggedUserUseCase: GetLoggedUserUseCase,
        @inject(GetAllGlobalEntitiesUseCase)
        private readonly getAllGlobalEntitiesUseCase: GetAllGlobalEntitiesUseCase,
    ) {
        super();
        makeObservable(this);
    }

    private get user(): AuthenticatedUser {
        return this.getLoggedUserUseCase.execute();
    }

    override async didMount(): Promise<void> {
        await super.didMount();

        if (this.user.isSuperAdmin() || this.user.isAdminRole()) {
            const globalEntities =
                await this.getAllGlobalEntitiesUseCase.execute();
            runInAction(() => {
                this.globalEntities = globalEntities;
            });
        }

        runInAction(() => {
            this.initialLoading = false;
        });
    }
}
